import { useEffect, useMemo, useState } from 'react';
import dayjs from 'dayjs';
import ModalComponent from 'components/Common/Modal';
import normalize from 'components/Dashboard/utilities/normalizeString';
import type { UploadHubListItem } from 'api/hooks/useUploadFiles';
import { getFileBadge } from 'components/UploadHub/fileTypeBadges';
import { DateFilter } from 'components/Dashboard/Filters/ui/DateFilter';
import { UKDatesFilters } from 'components/Dashboard/Filters/FiltersData/DatesFilters';
import {
  EMPTY_UPLOAD_HUB_FILTERS,
  UPLOAD_DATE_PRESET_LABELS,
  uploadHubActiveFiltersCount,
  type UploadHubFiltersState,
} from './uploadHubFiltersUtils';
import CustomScrollbar from 'components/Common/CustomScrollbar';
import useWindowHeight from 'components/Dashboard/utilities/useWindowHeight';

interface UploadHubFiltersProps {
  files: UploadHubListItem[];
  filters: UploadHubFiltersState;
  onApply: (filters: UploadHubFiltersState) => void;
}

const toggleValue = (list: string[], value: string) =>
  list.includes(value) ? list.filter((item) => item !== value) : [...list, value];

// The Before/After/Custom range options Monitor Inbox/search-results use — same source, no redefinition.
type CalendarDateOption = { name: string; filterProperty: string; showCalendar: boolean; dates?: string };
const CALENDAR_DATE_OPTIONS = (UKDatesFilters.find((f) => f.searchState === 'not-inbox')?.options ?? []).filter(
  (option: any) => option.showCalendar
) as CalendarDateOption[];

// DateOptions (from the shared DateFilter) identifies options by display name, not by our
// `datePreset` slug — normalize() (already used above for checkbox ids) bridges the two,
// e.g. normalize('Last 7 days') === 'last-7-days', normalize('Before') === 'before'.
const DATE_FILTER_NAMES = [...UPLOAD_DATE_PRESET_LABELS, ...CALENDAR_DATE_OPTIONS.map((option) => option.name)];
const nameForDatePreset = (datePreset: string) =>
  DATE_FILTER_NAMES.find((name) => normalize(name) === datePreset) ?? '';

const UploadHubFilters = (props: UploadHubFiltersProps) => {
  const { files, filters, onApply } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [draft, setDraft] = useState(filters);

  useEffect(() => {
    if (isOpen) setDraft(filters);
  }, [isOpen, filters]);

  const fileTypeOptions = useMemo(() => {
    const labels = new Set<string>();
    files.forEach((file) => labels.add(getFileBadge({ contentType: file.contentType }).label));
    return Array.from(labels).sort();
  }, [files]);

  const uploaderOptions = useMemo(() => {
    const names = new Set<string>();
    files.forEach((file) => file.addedByUserFullName && names.add(file.addedByUserFullName));
    return Array.from(names).sort();
  }, [files]);

  const appliedCount = uploadHubActiveFiltersCount(filters);
  const draftCount = uploadHubActiveFiltersCount(draft);

  const handleApply = () => {
    onApply(draft);
    setIsOpen(false);
  };

  const handleClear = () => {
    if (appliedCount > 0) {
      onApply(EMPTY_UPLOAD_HUB_FILTERS);
      setIsOpen(false);
    } else {
      setDraft(EMPTY_UPLOAD_HUB_FILTERS);
    }
  };

  const dateFilterOptions = [
    ...UPLOAD_DATE_PRESET_LABELS.map((name) => ({ name })),
    ...CALENDAR_DATE_OPTIONS.map((option) => {
      if (normalize(option.name) !== draft.datePreset) return option;
      const dates =
        option.filterProperty === 'range'
          ? draft.dateFrom && draft.dateTo && `${draft.dateFrom},${draft.dateTo}`
          : option.filterProperty === 'DATE_LT'
            ? draft.dateTo
            : draft.dateFrom;
      return dates ? { ...option, dates } : option;
    }),
  ];

  const dateFilter = {
    name: 'Upload date',
    options: dateFilterOptions,
    optionSelected: nameForDatePreset(draft.datePreset),
  };

  const selectDateOption = ({ newValue }: { newValue: { optionSelected: string; options: CalendarDateOption[] } }) => {
    const { optionSelected, options } = newValue;
    const datePreset = normalize(optionSelected) ?? '';
    const selected = options.find((option) => option.name === optionSelected);
    if (!optionSelected || !selected?.dates) {
      setDraft({ ...draft, datePreset, dateFrom: '', dateTo: '' });
    } else if (selected.filterProperty === 'range') {
      const [from, to] = selected.dates.split(',');
      setDraft({
        ...draft,
        datePreset,
        dateFrom: dayjs(from).format('YYYY-MM-DD'),
        dateTo: dayjs(to).format('YYYY-MM-DD'),
      });
    } else if (selected.filterProperty === 'DATE_LT') {
      setDraft({ ...draft, datePreset, dateFrom: '', dateTo: dayjs(selected.dates).format('YYYY-MM-DD') });
    } else {
      setDraft({ ...draft, datePreset, dateFrom: dayjs(selected.dates).format('YYYY-MM-DD'), dateTo: '' });
    }
  };
  const { windowHeight } = useWindowHeight();
  const heightValue = (windowHeight * 90) / 100 - 150;

  return (
    <div className='position-relative filter-main-container'>
      <button
        className={`sort-button more-filters-button ${appliedCount > 0 ? 'more-filters-button-active' : ''}`}
        onClick={() => setIsOpen(true)}
      >
        <span className='icon-filters'></span>
        <span className='d-none d-md-inline-block'>Filters</span> {appliedCount > 0 && <span>({appliedCount})</span>}
      </button>
      <ModalComponent isOpen={isOpen} setIsOpen={setIsOpen}>
        <CustomScrollbar
          autoHeight
          autoHeightMin='100%'
          autoHeightMax={`${heightValue}px`}
          className={'simple-scrollbar'}
        >
          <div className='px-4 pt-4 pb-3 filters-popup-container'>
            {fileTypeOptions.length > 0 && (
              <div className='filters-section'>
                <div className='filters-section-container pt-2'>
                  <h3>File type</h3>
                  {fileTypeOptions.map((type) => (
                    <div className='checkbox-item mb-2' key={type}>
                      <input
                        id={`upload-hub-file-type-${normalize(type)}`}
                        type='checkbox'
                        checked={draft.fileTypes.includes(type)}
                        onChange={() => setDraft({ ...draft, fileTypes: toggleValue(draft.fileTypes, type) })}
                      />
                      <label htmlFor={`upload-hub-file-type-${normalize(type)}`}>{type}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div className='filters-section'>
              <div className='filters-section-container'>
                <DateFilter filter={dateFilter} selectOption={selectDateOption} />
              </div>
            </div>
            {uploaderOptions.length > 0 && (
              <div className='filters-section'>
                <div className='filters-section-container'>
                  <h3>Uploaded by</h3>
                  {uploaderOptions.map((name) => (
                    <div className='checkbox-item mb-2' key={name}>
                      <input
                        id={`upload-hub-uploader-${normalize(name)}`}
                        type='checkbox'
                        checked={draft.uploaders.includes(name)}
                        onChange={() => setDraft({ ...draft, uploaders: toggleValue(draft.uploaders, name) })}
                      />
                      <label htmlFor={`upload-hub-uploader-${normalize(name)}`}>{name}</label>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </CustomScrollbar>
        <div className='d-flex justify-content-between align-content-center border-top px-4 px-lg-5 py-2 py-lg-4'>
          <button
            className='simple-link-button general-button'
            onClick={handleClear}
            style={{ visibility: draftCount > 0 || appliedCount > 0 ? 'visible' : 'hidden' }}
          >
            Clear filters
          </button>
          <button className='general-button action-button px-4' onClick={handleApply}>
            Apply filters
          </button>
        </div>
      </ModalComponent>
    </div>
  );
};

export default UploadHubFilters;
